import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ProductKitClient } from '@frontend/product/api';
import { IProduct, ProductExtended } from '@frontend/product/types';
import { useState } from 'react';

export interface LinkKitItemsModalProps {
    modalId: string;
    product: ProductExtended;
    onLinked: () => void;
}

export const useLinkKitItemsModal = (props: LinkKitItemsModalProps) => {
    const { closeModal } = useModals();
    const [linkProducts, setLinkProductsState] = useState<IProduct[]>([]);
    const [quantities, setQuantities] = useState<Record<string, number>>({});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const setLinkProducts = (products: IProduct[]) => {
        setLinkProductsState(products);
        setQuantities((prev) => {
            const next: Record<string, number> = {};
            products.forEach((p) => {
                next[p.id] = prev[p.id] ?? 1;
            });
            return next;
        });
    };

    const setQuantity = (productId: string, quantity: number) => {
        setQuantities((prev) => ({ ...prev, [productId]: Math.max(1, quantity || 1) }));
    };

    const onCancel = () => {
        closeModal(props.modalId);
    };

    const onSubmit = async () => {
        if (isSubmitting || linkProducts.length === 0) return;
        setIsSubmitting(true);

        try {
            await Promise.all(
                linkProducts.map((product) =>
                    ProductKitClient.postProductKit(props.product.account_id, {
                        kit_id: props.product.id,
                        product_id: product.id,
                        quantity: quantities[product.id] ?? 1
                    })
                )
            );
            props.onLinked();
            closeModal(props.modalId);
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        linkProducts,
        setLinkProducts,
        quantities,
        setQuantity,
        isSubmitting,
        onCancel,
        onSubmit
    };
};
